import { LogisticsProjectLookupComponent } from '../../../logistics-projects/logistics-project-lookup.component';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Location, CommonModule } from '@angular/common';
import { MenuItem, MessageService, SelectItem } from "primeng/api";
import { tap, forkJoin, map, of, switchMap } from 'rxjs';

// PrimeNG Modules
import { ToastModule } from 'primeng/toast';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ToolbarModule } from 'primeng/toolbar';
import { CardModule } from 'primeng/card';
import { InputNumberModule } from 'primeng/inputnumber';
import { TextareaModule } from 'primeng/textarea';
import { SelectChangeEvent, SelectModule } from 'primeng/select';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { TableModule } from 'primeng/table';
import { DatePickerModule } from 'primeng/datepicker';

import { Company } from "../../../company/company.model";
import { Customer } from "../../../customer/customer.model";
import { CustomerService } from "../../../customer/customer.service";
import { PaymentTerm } from "../../models/payment-term.model";
import { Products } from "../../models/product.model";
import { OrdersService } from "../../order.service";
import { ContractsService } from "../../../contracts/contracts.service";
import { SessionStorageService } from "../../../services/session-storage.service";
import { PurchaseOrderLine } from "../purchase-order-line.model";
import { PurchaseOrder } from "../purchase-order.model";
import { ProductsService } from "../../product.service";
import { ProductVariant } from "../../models/product-variant.model";
import { currencyFractionDigits, orderLineAmounts, roundCurrency, unitPriceForTargetNet } from '../../../shared/utils/commercial-money';
import { DividerModule } from 'primeng/divider';
import { ProgressBarModule } from "primeng/progressbar";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { IftaLabelModule } from "primeng/iftalabel";
import { TooltipModule } from 'primeng/tooltip';
import { PartyService } from 'src/app/party/party.service';
import { Party } from 'src/app/party/party.model';
import { PartyType } from 'src/app/party/party-type.enum';
import { FlexFieldValue } from 'src/app/shared/flex-fields/flex-field.model';
import { FlexFieldFormComponent } from 'src/app/shared/flex-fields/flex-field-form/flex-field-form.component';
import { CustomerFormComponent } from 'src/app/customer/customer-form/customer-form.component';
import { CompanyFormComponent } from 'src/app/company/company-form/company-form.component';
import { PageShellComponent } from 'src/app/shared/page-layout/page-shell.component';
import { PageHeroComponent } from 'src/app/shared/page-layout/page-hero.component';
import { PageFormSectionComponent } from 'src/app/shared/page-layout/page-form-section.component';
import { PageMetricStripComponent } from 'src/app/shared/page-layout/page-metric-strip.component';
import { PageMetric } from 'src/app/shared/page-layout/page-layout.models';
import { LogisticsProjectsService } from 'src/app/logistics-projects/logistics-projects.service';
import { EditorModule } from 'primeng/editor';
import { CrmClient, CrmClientCommunication } from 'src/app/crm/crm.model';
import { CrmService } from 'src/app/crm/crm.service';
import { EnterpriseFeatureService } from 'src/app/services/enterprise-features.service';
import { AiParsedOrderDocument, AiParsedOrderLine, OrderDocumentAiService } from '../../order-document-ai.service';
import { FirebaseStorageService } from 'src/app/services/firebase-storage.service';
import { PurchaseOrderAttachment } from '../purchase-order-attachment.model';
import { AddEditProductComponent } from '../../product/add-edit-product/add-edit-product.component';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CommercialPartyPickerComponent } from 'src/app/shared/commercial-party-picker/commercial-party-picker.component';
import { CommercialPartyReference } from 'src/app/shared/commercial-party-picker/commercial-party.model';
import { CrmQuickCreateResult } from 'src/app/shared/crm-quick-create/crm-quick-create-dialog.component';
import { CRM_PAYABLE_ROLES } from 'src/app/shared/crm-client-contact-picker/crm-party-context.service';
// DropdownChangeEvent is not a module, it's an interface, so no change needed here.
// import {DropdownChangeEvent} from "primeng/dropdown";

type PartySourceMode = 'CRM' | 'MANUAL';

@Component({
    selector: 'app-add-edit-purch-order',
    templateUrl: './add-edit-purch-order.component.html',
    styleUrls: ['./add-edit-purch-order.component.css'],
    standalone: true,
    imports: [
        LogisticsProjectLookupComponent,
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    ToastModule,
    InputTextModule,
    ButtonModule,
    ToolbarModule,
    CardModule,
    InputNumberModule,
    TextareaModule,
    SelectModule,
    BreadcrumbModule,
    TableModule,
    DatePickerModule,
    DividerModule,
    ProgressBarModule,
    InputGroupModule,
    InputGroupAddonModule,
        IftaLabelModule,
        TooltipModule,
    FlexFieldFormComponent,
    CustomerFormComponent,
    CompanyFormComponent,
    PageShellComponent,
    PageHeroComponent,
    PageFormSectionComponent,
    PageMetricStripComponent,
    EditorModule,
    CommercialPartyPickerComponent
],
    providers: [
        MessageService,
        OrdersService,
        ProductsService,
        CustomerService,
        CrmService,
        ContractsService,
        DialogService
    ]
})
export class AddEditPurchOrderComponent implements OnInit {

    readonly payablePartyRoles = CRM_PAYABLE_ROLES;
    commercialPartyReference: CommercialPartyReference = { source: 'CRM' };

    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = {};

    purchaseOrderForm!: FormGroup;
    priceTargetLine: AbstractControl | null = null;
    targetNetAmount: number | null = null;
    editMode = false;
    isLocked = false;
    lockReason = '';
    purchaseOrderId!: number;
    isLoading: boolean = false;
    isAiParsing = false;
    aiExtraction: AiParsedOrderDocument | null = null;
    aiExtractionUnmatchedProducts = 0;
    aiExtractionCounterpartyMatched = false;
    partySourceMode: PartySourceMode = 'CRM';

    companies: SelectItem<Company>[] = [];
    vendors: SelectItem<Customer>[] = [];
    mainParties: SelectItem<Party>[] = [];
    subParties: SelectItem<Party>[] = [];
    crmClients: SelectItem<CrmClient>[] = [];
    paymentTerms: SelectItem<PaymentTerm>[] = [];
    products: SelectItem<Products>[] = [];
    productVariants: SelectItem<ProductVariant>[] = [];

    currentOwningCompanyId?: number;
    currentPurchaseOrder?: PurchaseOrder;
    headerFlexFieldValues: FlexFieldValue[] = [];
    lineFlexFieldValues: FlexFieldValue[][] = [];
    subtotal = 0;
    tax = 0;
    total = 0;
    currencyCode = 'SAR';
    showCompanyForm = false;
    companyFormHeader = 'Create Company';
    companyFormSubmitLabel = 'Create';
    showVendorForm = false;
    vendorFormHeader = 'Create Vendor';
    vendorFormSubmitLabel = 'Create';
    selectedCompany: Company = new Company();
    selectedVendor: Customer = new Customer();
    crmClientContacts: CrmClientCommunication[] = [];
    private pendingAiContact?: { name?: string | null; email?: string | null; phone?: string | null };
    private pendingAiSourceDocument?: File;
    quickMappingLine: number | null = null;
    private productDialogRef?: DynamicDialogRef;

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private location: Location,
        private partyService: PartyService,
        private messageService: MessageService,
        private productsService: ProductsService,
        private purchaseOrderService: OrdersService,
        private customerService: CustomerService,
        private crmService: CrmService,
        private enterpriseFeatureService: EnterpriseFeatureService,
        private contractService: ContractsService,
        private sessionStorageService: SessionStorageService,
        private logisticsProjectsService: LogisticsProjectsService,
        private router: Router,
        private orderDocumentAiService: OrderDocumentAiService,
        private firebaseStorageService: FirebaseStorageService,
        private dialogService: DialogService
    ) {
    }

    ngOnInit(): void {
        this.breadcrumbItems = [
            { label: 'Orders', routerLink: '/order/purchase-order' },
            { label: 'Purchase Order', routerLink: '/order/purchase-order' },
            { label: this.editMode ? 'Edit Purchase Order' : 'Add Purchase Order' }
        ];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };

        this.currentOwningCompanyId = this.sessionStorageService.getCompany()?.id ?? undefined;

        this.initForm();
        this.applyPartyValidators();

        this.route.params.subscribe(params => {
            this.purchaseOrderId = params['id'];
            this.editMode = !!this.purchaseOrderId;
            this.loadData();
        });

        if (!this.enterpriseFeatureService.isLoaded()) {
            this.enterpriseFeatureService.getAllSubscriptions().subscribe(() => {
                this.applyPartyValidators();
                this.loadPartyOptionsForMode();
            });
        } else {
            this.loadPartyOptionsForMode();
        }
        this.syncLineFlexFieldValues();
    }

    loadData() {
        this.isLoading = true;

        const loadObservables = {
            products: this.loadProducts(),
            paymentTerms: this.loadPaymentTerms(),
        };

        forkJoin(loadObservables)
            .pipe(
                tap(() => {
                    if (this.editMode) {
                        this.loadPurchaseOrder().subscribe();
                    }
                })
            )
            .subscribe({
                error: (error) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'An error occurred while loading data.' + error.message
                    });
                    this.isLoading = false;
                },
                complete: () => {
                    this.isLoading = false;
                }
            });
    }

    loadProducts() {
        return this.productsService.getSharedCatalog(this.sessionStorageService.getCompany()!.id).pipe(
            tap(products => {
                this.products = products.map((product) => ({
                    value: product,
                    label: product.name
                }));
                this.hydrateLineProductSelections();
            })
        );
    }

    loadPaymentTerms() {
        const companyId = this.currentOwningCompanyId ?? this.sessionStorageService.getCompany()?.id;
        return this.purchaseOrderService.getAllPaymentTerms(companyId).pipe(
            tap(paymentTerms => {
                this.paymentTerms = paymentTerms.map((paymentTerm) => ({
                    value: paymentTerm,
                    label: paymentTerm.name
                }));
            })
        );
    }

    loadCompanies() {
        return this.partyService.getParties(this.sessionStorageService.getCompany()?.id ?? 0, ['Company'], '', 0, 1000).pipe(
            tap(value => {
                this.companies = value.content.map(party => this.partyService.convertPartyToCompany(party)).map(company => ({
                    value: company,
                    label: company.name
                }));
            })
        );
    }

    private loadMainParties(): void {
        const companyId = this.sessionStorageService.getCompany()?.id ?? 0;
        if (!companyId) {
            this.mainParties = [];
            return;
        }
        this.partyService.getParties(companyId, [], '', 0, 1000).subscribe(result => {
            this.mainParties = result.content.map(party => ({ value: party, label: this.partyLabel(party) }));
        });
    }

    onMainPartyChange(event: SelectChangeEvent, preserveSubParty = false): void {
        const party = event.value as Party | null;
        if (!preserveSubParty) {
            this.purchaseOrderForm.patchValue({ subParty: null });
        }
        if (!party || party.id.entityType !== PartyType.Company) {
            this.subParties = [];
            return;
        }
        const owningCompanyId = this.sessionStorageService.getCompany()?.id ?? 0;
        this.customerService.getCustomersByCompany(party.id.entityId).subscribe(customers => {
            this.subParties = customers.map(customer => {
                const contact = this.partyService.convertToParty(customer, PartyType.Customer, owningCompanyId);
                return { value: contact, label: this.partyLabel(contact) };
            });
        });
    }

    isCompanyMainParty(): boolean {
        return (this.purchaseOrderForm?.get('mainParty')?.value as Party | null)?.id?.entityType === PartyType.Company;
    }

    private partyLabel(party: Party): string {
        return `${party.entityName || 'Unnamed party'} (${party.id.entityType})`;
    }

    onCompanyChange(event: SelectChangeEvent) {
        if (this.crmActive()) {
            return;
        }
        const company = event.value as Company;
        if (company) {
            this.currentOwningCompanyId = company.id;
            this.selectedCompany = company;
            const shouldResetVendor = !this.editMode || this.currentPurchaseOrder?.company?.id !== company.id;
            if (shouldResetVendor) {
                this.selectedVendor = new Customer();
                this.headerFlexFieldValues = [];
                this.lineFlexFieldValues = this.getPurchaseOrderLineControls.controls.map(() => []);
            }
            if (!shouldResetVendor && this.purchaseOrderForm.get('vendor')?.value) {
                this.selectedVendor = this.purchaseOrderForm.get('vendor')?.value as Customer;
            }
            this.loadVendorsByCompany(company).subscribe();
        } else {
            this.vendors = [];
            this.purchaseOrderForm.patchValue({ vendor: null });
            this.currentOwningCompanyId = undefined;
            this.headerFlexFieldValues = [];
            this.lineFlexFieldValues = [];
            this.selectedCompany = new Company();
            this.selectedVendor = new Customer();
        }
        this.syncLineFlexFieldValues();
    }

    loadVendorsByCompany(company: Company) {
        return this.crmService.getClients(company.id, ['%supplier%', '%vendor%']).pipe(
            tap(clients => {
                this.vendors = clients.map(client => this.crmClientToSupplierOption(client));
                if (this.selectedVendor?.id) {
                    const exists = this.vendors.some(v => v.value?.id === this.selectedVendor.id);
                    if (!exists) {
                        this.vendors.push({ value: this.selectedVendor, label: this.selectedVendor.name });
                    }
                }
            })
        );
    }

    loadPurchaseOrder() {
        return this.purchaseOrderService.getPurchaseOrderById(this.purchaseOrderId).pipe(
            tap(purchaseOrder => {
                this.currentPurchaseOrder = purchaseOrder;
                this.headerFlexFieldValues = purchaseOrder.flexFieldValues ?? [];
                this.currentOwningCompanyId = purchaseOrder.owningCompanyId ?? purchaseOrder.company?.id ?? this.currentOwningCompanyId;
                const billingStatus = (purchaseOrder.billingStatus || '').toUpperCase();
                const state = (purchaseOrder.state || '').toUpperCase();
                const billGenerated = !!purchaseOrder.billGenerated || state === 'BILLED'
                    || ['BILL_DRAFTED', 'BILLED', 'PARTIALLY_BILLED'].includes(billingStatus);
                if (billGenerated) {
                    this.isLocked = true;
                    this.lockReason = 'This purchase order cannot be edited because a vendor bill has already been generated.';
                    this.purchaseOrderForm.disable();
                    this.messageService.add({
                        severity: 'warn',
                        summary: 'Order Locked',
                        detail: this.lockReason
                    });
                }
                const orderDate = purchaseOrder.orderDate ? new Date(purchaseOrder.orderDate) : null;
                const expectedDate = purchaseOrder.expectedDate ? new Date(purchaseOrder.expectedDate) : null;
                this.selectedCompany = purchaseOrder.company ?? new Company();
                this.selectedVendor = (purchaseOrder as any).vendor ?? purchaseOrder.supplier ?? new Customer();
                let matchedPaymentTerm: PaymentTerm | null = null;
                if (purchaseOrder.paymentTerm) {
                    const found = this.paymentTerms.find(opt => opt.value?.paymentTermID === purchaseOrder.paymentTerm.paymentTermID);
                    if (found) {
                        matchedPaymentTerm = found.value;
                    } else {
                        matchedPaymentTerm = purchaseOrder.paymentTerm;
                        this.paymentTerms.push({ value: matchedPaymentTerm, label: matchedPaymentTerm.name });
                    }
                }
                const externalRef = purchaseOrder.externalReferenceNumber ?? purchaseOrder.supplierReference ?? '';
                this.purchaseOrderForm.patchValue({
                    purchaseOrderID: purchaseOrder.purchaseOrderID ?? null,
                    orderNumber: (purchaseOrder as any).orderNumber ?? purchaseOrder.purchaseOrderNumber ?? '',
                    crmClient: purchaseOrder.crmClient ?? null,
                    crmAccountId: purchaseOrder.crmAccountId ?? null,
                    crmClientContactId: purchaseOrder.crmClientContactId ?? purchaseOrder.crmClientContact?.id ?? null,
                    mainParty: purchaseOrder.mainParty ?? null,
                    subParty: purchaseOrder.subParty ?? null,
                    company: purchaseOrder.company ?? null,
                    vendor: (purchaseOrder as any).vendor ?? purchaseOrder.supplier ?? null,
                    paymentTerm: matchedPaymentTerm,
                    orderDate,
                    expectedDate,
                    buyerId: purchaseOrder.buyerId ?? null,
                    requesterId: purchaseOrder.requesterId ?? null,
                    department: purchaseOrder.department ?? '',
                    costCenter: purchaseOrder.costCenter ?? '',
                    projectReference: purchaseOrder.projectReference ?? '',
                    procurementCategory: purchaseOrder.procurementCategory ?? '',
                    deliveryTerms: purchaseOrder.deliveryTerms ?? '',
                    incoterms: purchaseOrder.incoterms ?? '',
                    supplierReference: externalRef,
                    externalReferenceNumber: externalRef,
                    supplierContact: purchaseOrder.supplierContact ?? '',
                    promisedDate: purchaseOrder.promisedDate ? new Date(purchaseOrder.promisedDate) : null,
                    supplierConfirmationStatus: purchaseOrder.supplierConfirmationStatus ?? '',
                    logisticsProjectId: purchaseOrder.logisticsProjectId ?? null,
                    currencyCode: purchaseOrder.currencyCode ?? this.currencyCode,
                    exchangeRate: purchaseOrder.exchangeRate ?? 1,
                    notes: purchaseOrder.notes ?? ''
                });
                this.partySourceMode = purchaseOrder.crmClient?.id || purchaseOrder.crmClientId ? 'CRM' : 'MANUAL';
                this.commercialPartyReference = {
                    source: purchaseOrder.crmClient?.id || purchaseOrder.crmClientId ? 'CRM' : 'LEGACY',
                    crmClientId: purchaseOrder.crmClient?.id ?? purchaseOrder.crmClientId,
                    crmAccountId: purchaseOrder.crmAccountId,
                    crmContactId: purchaseOrder.crmClientContactId ?? purchaseOrder.crmClientContact?.id,
                    legacyCompanyId: purchaseOrder.mainParty?.id?.entityType === PartyType.Company ? purchaseOrder.mainParty.id.entityId : undefined,
                    legacyCustomerId: purchaseOrder.subParty?.id?.entityId
                        ?? (purchaseOrder.mainParty?.id?.entityType === PartyType.Customer ? purchaseOrder.mainParty.id.entityId : undefined)
                };
                this.applyPartyValidators();
                if (purchaseOrder.mainParty && this.isManualPartySource()) {
                    this.onMainPartyChange({ value: purchaseOrder.mainParty } as SelectChangeEvent, true);
                }
                this.currencyCode = purchaseOrder.currencyCode || this.currencyCode;
                this.crmClientContacts = purchaseOrder.crmClientContacts ?? [];
                if (purchaseOrder.crmClient) {
                    this.loadCrmClientContacts(purchaseOrder.crmClient);
                    this.loadCrmClients().subscribe();
                } else if (purchaseOrder.company && this.isManualPartySource()) {
                    this.onCompanyChange({ value: purchaseOrder.company } as SelectChangeEvent);
                }
                const lines = purchaseOrder.purchaseOrderLines ?? [];
                const lineGroups = lines.length
                    ? lines.map(line => this.createPurchaseOrderLineFormGroup(line))
                    : [this.createPurchaseOrderLineFormGroup()];
                this.purchaseOrderForm.setControl('purchaseOrderLines', this.fb.array(lineGroups));
                this.hydrateLineProductSelections();
                this.getPurchaseOrderLineControls.valueChanges.subscribe(() => this.calculateTotals());
                this.lineFlexFieldValues = lines.length ? lines.map(line => line.flexFieldValues ?? []) : [[]];
                this.syncLineFlexFieldValues();
                this.calculateTotals();
            })
        );
    }

    loadCrmClients() {
        const companyId = this.sessionStorageService.getCompany()?.id ?? this.currentOwningCompanyId ?? 0;
        if (!companyId) {
            this.crmClients = [];
            this.crmClientContacts = [];
            return of([]);
        }
        return this.crmService.getClients(companyId, ['%supplier%', '%vendor%']).pipe(
            tap(clients => {
                this.crmClients = clients.map(client => ({
                    value: client,
                    label: `${client.companyName || client.notifyingPersonName || 'CRM Client'}${client.clientNumber ? ` (${client.clientNumber})` : ''}`,
                    disabled: !client.linkedCompanyId && !client.linkedCustomerId
                }));
                const selectedClient = this.purchaseOrderForm.get('crmClient')?.value as CrmClient | null;
                if (selectedClient?.id && !this.crmClients.some(option => option.value?.id === selectedClient.id)) {
                    this.crmClients.push({
                        value: selectedClient,
                        label: `${selectedClient.companyName || selectedClient.notifyingPersonName || 'CRM Client'}${selectedClient.clientNumber ? ` (${selectedClient.clientNumber})` : ''}`
                    });
                }
            })
        );
    }

    onCrmClientChange(event: SelectChangeEvent) {
        const crmClient = event.value as CrmClient | null;
        this.partySourceMode = 'CRM';
        this.applyPartyValidators();
        if (!crmClient) {
            this.crmClientContacts = [];
            this.purchaseOrderForm.patchValue({ mainParty: null, subParty: null, company: null, vendor: null, crmClientContactId: null });
            this.refreshClientItemCodes();
            return;
        }
        this.purchaseOrderForm.patchValue({ mainParty: null, subParty: null, company: null, vendor: null, crmClientContactId: null });
        this.loadCrmClientContacts(crmClient);
        this.refreshClientItemCodes();
    }

    onCrmClientPicked(crmClient: CrmClient | null): void {
        this.purchaseOrderForm.patchValue({ crmClient });
        this.onCrmClientChange({ value: crmClient } as SelectChangeEvent);
    }

    onCrmContactPicked(contact: CrmClientCommunication | null): void {
        this.purchaseOrderForm.patchValue({ crmClientContactId: contact?.id ?? null });
    }

    get commercialPartyOwningCompanyId(): number | undefined {
        return this.sessionStorageService.getCompany()?.id ?? this.currentOwningCompanyId;
    }

    onCrmQuickCreated(result: CrmQuickCreateResult): void {
        if (result.mode === 'client') {
            const client = result.record;
            this.crmClients = [...this.crmClients, {
                value: client,
                label: `${client.companyName}${client.clientNumber ? ` (${client.clientNumber})` : ''}`,
                disabled: !client.linkedCompanyId && !client.linkedCustomerId
            }];
        }
    }

    onCommercialPartyChange(reference: CommercialPartyReference): void {
        this.commercialPartyReference = reference;
        if (reference.source === 'CRM') {
            this.setPartySourceMode('CRM');
            const crmClient = this.crmClients.find(option => option.value?.id === reference.crmClientId)?.value
                ?? (reference.crmClientId ? ({ id: reference.crmClientId } as CrmClient) : null);
            this.purchaseOrderForm.patchValue({ crmClient });
            this.onCrmClientChange({ value: crmClient } as SelectChangeEvent);
            this.purchaseOrderForm.patchValue({
                crmAccountId: reference.crmAccountId ?? null,
                crmClientContactId: reference.crmContactId ?? null
            });
            return;
        }
        this.setPartySourceMode('MANUAL');
        const mainType = reference.legacyCompanyId ? PartyType.Company : PartyType.Customer;
        const mainId = reference.legacyCompanyId ?? reference.legacyCustomerId;
        const mainParty = this.mainParties.find(option =>
            option.value?.id.entityType === mainType && option.value.id.entityId === mainId)?.value ?? null;
        const subParty = reference.legacyCompanyId && reference.legacyCustomerId
            ? ({ id: { entityId: reference.legacyCustomerId, entityType: PartyType.Customer }, entityName: '', isCompany: false,
                owningCompanyId: this.commercialPartyOwningCompanyId } as Party)
            : null;
        this.purchaseOrderForm.patchValue({ mainParty, subParty, crmAccountId: null, crmClientContactId: null });
        this.applyPartyValidators();
    }

    get crmClientValues(): CrmClient[] {
        return this.crmClients.map(option => option.value).filter((client): client is CrmClient => !!client);
    }

    get selectedCrmContact(): CrmClientCommunication | null {
        const selectedId = Number(this.purchaseOrderForm?.get('crmClientContactId')?.value || 0);
        return this.crmClientContacts.find(contact => contact.id === selectedId) ?? null;
    }

    loadCrmClientContacts(crmClient: CrmClient | null): void {
        if (!crmClient?.id) {
            this.crmClientContacts = [];
            this.purchaseOrderForm.patchValue({ crmClientContactId: null }, { emitEvent: false });
            return;
        }
        this.crmService.getClientMasterCommunications(crmClient.id).subscribe(contacts => {
            this.crmClientContacts = contacts ?? [];
            const parsedContact = this.findAiCrmContact(this.crmClientContacts);
            this.purchaseOrderForm.patchValue({
                crmClientContactId: parsedContact?.id || this.resolvePreferredCrmContactId(this.purchaseOrderForm.get('crmClientContactId')?.value)
            }, { emitEvent: false });
            this.pendingAiContact = undefined;
        });
    }

    private findAiCrmContact(contacts: CrmClientCommunication[]): CrmClientCommunication | undefined {
        if (!this.pendingAiContact) {
            return undefined;
        }
        return contacts.find(contact => this.matchesAiText(this.pendingAiContact?.email, contact.email)
            || this.matchesAiText(this.pendingAiContact?.phone, contact.phone, contact.mobile)
            || this.matchesAiText(this.pendingAiContact?.name, contact.name));
    }

    initForm() {
        this.purchaseOrderForm = this.fb.group({
            purchaseOrderID: [null],
            orderNumber: [''],
            crmClient: [null],
            crmAccountId: [null],
            crmClientContactId: [null],
            mainParty: [null],
            subParty: [null],
            company: [null],
            vendor: [null],
            paymentTerm: [null, Validators.required],
            orderDate: [new Date(), Validators.required],
            expectedDate: [new Date(), Validators.required],
            buyerId: [null],
            requesterId: [null],
            department: [''],
            costCenter: [''],
            projectReference: [''],
            procurementCategory: [''],
            deliveryTerms: [''],
            incoterms: [''],
            supplierReference: [''],
            externalReferenceNumber: [''],
            supplierContact: [''],
            promisedDate: [null],
            supplierConfirmationStatus: [''],
            logisticsProjectId: [Number(this.route.snapshot.queryParamMap.get('projectId') || '') || null],
            currencyCode: [this.currencyCode],
            exchangeRate: [1],
            notes: [''],
            flexFields: this.fb.array([]),
            purchaseOrderLines: this.fb.array([this.createPurchaseOrderLineFormGroup()]),
        });
        this.headerFlexFieldValues = [];
        this.syncLineFlexFieldValues();
        this.getPurchaseOrderLineControls.valueChanges.subscribe(() => this.calculateTotals());
        this.purchaseOrderForm.get('currencyCode')?.valueChanges.subscribe(value => {
            this.currencyCode = value || 'SAR';
            this.calculateTotals();
        });
    }

    createPurchaseOrderLineFormGroup(purchaseOrderLine?: PurchaseOrderLine): FormGroup {
        const formGroup = this.fb.group({
            lineID: [purchaseOrderLine?.lineID ?? null],
            orderID: [purchaseOrderLine?.orderID ?? null],
            lineNumber: [purchaseOrderLine?.lineNumber ?? null],
            product: [purchaseOrderLine?.product ?? null, Validators.required],
            variant: [purchaseOrderLine?.variant ?? null, Validators.required],
            description: [purchaseOrderLine?.description ?? ''],
            quantity: [purchaseOrderLine?.quantity ?? 1, [Validators.required, Validators.min(1)]],
            unitPrice: [purchaseOrderLine?.unitPrice ?? 0, [Validators.required, Validators.min(0)]],
            displayUnitPrice: [Number(Number(purchaseOrderLine?.unitPrice ?? 0).toFixed(4))],
            appliedLineDiscount: [purchaseOrderLine?.appliedLineDiscount ?? 0],
            logisticsProjectId: [purchaseOrderLine?.logisticsProjectId ?? null],
            inventoryItemId: [purchaseOrderLine?.inventoryItemId ?? null],
            orderUomId: [purchaseOrderLine?.orderUomId ?? null],
            baseUomId: [purchaseOrderLine?.baseUomId ?? null],
            conversionFactorToBase: [purchaseOrderLine?.conversionFactorToBase ?? 1],
            orderedBaseQuantity: [purchaseOrderLine?.orderedBaseQuantity ?? null],
            receivedQuantity: [purchaseOrderLine?.receivedQuantity ?? 0],
            receivedBaseQuantity: [purchaseOrderLine?.receivedBaseQuantity ?? 0],
            damagedQuantity: [purchaseOrderLine?.damagedQuantity ?? 0],
            rejectedQuantity: [purchaseOrderLine?.rejectedQuantity ?? 0],
            remainingQuantity: [purchaseOrderLine?.remainingQuantity ?? purchaseOrderLine?.quantity ?? 1],
            crmClientItemCode: [''],
            crmClientItemMappingId: [null],
            flexFields: this.fb.array([]),
        });
        // PrimeNG emits the four-decimal display value on blur; keep the saved rate separate.
        let displayedPrice = formGroup.get('displayUnitPrice')?.value;
        formGroup.get('unitPrice')?.valueChanges.subscribe(price => {
            displayedPrice = price == null ? null : Number(Number(price).toFixed(4));
            formGroup.get('displayUnitPrice')?.setValue(displayedPrice, { emitEvent: false });
        });
        formGroup.get('displayUnitPrice')?.valueChanges.subscribe(price => {
            if (price !== displayedPrice) formGroup.get('unitPrice')?.setValue(price);
        });
        this.syncVariantControl(formGroup);
        return formGroup;
    }

    productVariantsFor(lineGroup: AbstractControl): SelectItem<ProductVariant>[] {
        const product = lineGroup.get('product')?.value as Products | null;
        const selectedVariant = lineGroup.get('variant')?.value as ProductVariant | null;
        const variants = product?.productVariants ?? [];
        const options = selectedVariant && !variants.some(variant => variant.variantID === selectedVariant.variantID)
            ? [selectedVariant, ...variants]
            : variants;
        return options.map(variant => ({ value: variant, label: variant.name }));
    }

    private hydrateLineProductSelections(): void {
        if (!this.purchaseOrderForm) {
            return;
        }
        this.getPurchaseOrderLineControls.controls.forEach(control => {
            const lineGroup = control as FormGroup;
            const requestedProduct = lineGroup.get('product')?.value as Products | null;
            const product = this.catalogProduct(requestedProduct);
            const requestedVariant = lineGroup.get('variant')?.value as ProductVariant | null;
            const variant = this.catalogVariant(product, requestedVariant);

            lineGroup.patchValue({ product, variant }, { emitEvent: false });
            this.syncVariantControl(lineGroup);
            this.syncClientItemCode(lineGroup, product);
        });
    }

    private catalogProduct(product: Products | null | undefined): Products | null {
        if (!product?.productID) {
            return product ?? null;
        }
        const catalogProduct = this.products.find(option => option.value.productID === product.productID)?.value;
        if (catalogProduct) {
            return catalogProduct;
        }
        this.products = [...this.products, { value: product, label: product.name }];
        return product;
    }

    private catalogVariant(product: Products | null, variant: ProductVariant | null | undefined): ProductVariant | null {
        if (!variant?.variantID) {
            return variant ?? null;
        }
        return product?.productVariants?.find(candidate => candidate.variantID === variant.variantID) ?? variant;
    }

    get getPurchaseOrderLineControls(): FormArray {
        return this.purchaseOrderForm.get('purchaseOrderLines') as FormArray;
    }

    get headerFlexFieldControls(): FormArray {
        return this.purchaseOrderForm.get('flexFields') as FormArray;
    }

    getLineFlexFieldControls(index: number): FormArray {
        const control = this.getPurchaseOrderLineControls.at(index) as FormGroup;
        return control.get('flexFields') as FormArray;
    }

    formMetrics(): PageMetric[] {
        return [
            { label: 'Order Total', value: this.formatMoney(this.total), hint: this.currencyCode, tone: 'success' },
            { label: 'Lines', value: this.getPurchaseOrderLineControls.length, hint: 'Procurement lines', tone: 'info' },
            { label: 'Tax', value: this.formatMoney(this.tax), hint: 'Estimated from product tax' },
            { label: 'Expected', value: this.dateLabel(this.purchaseOrderForm?.get('expectedDate')?.value), hint: this.purchaseOrderForm?.get('paymentTerm')?.value?.name || 'No payment term', tone: 'warn' }
        ];
    }

    readinessPercent(): number {
        if (!this.purchaseOrderForm) {
            return 0;
        }
        const checks = this.readinessItems();
        const complete = checks.filter(item => item.done).length;
        return Math.round((complete / checks.length) * 100);
    }

    readinessItems(): { label: string; done: boolean; hint: string }[] {
        if (this.isCrmPartySource()) {
            const client = this.purchaseOrderForm?.get('crmClient')?.value as CrmClient | null;
            return [
                { label: 'CRM Vendor', done: !!client?.id, hint: client?.companyName || client?.notifyingPersonName || 'Required' },
                { label: 'CRM Contact', done: true, hint: this.selectedCrmContactLabel() || 'Optional' },
                { label: 'Terms', done: !!this.purchaseOrderForm?.get('paymentTerm')?.value, hint: this.purchaseOrderForm?.get('paymentTerm')?.value?.name || 'Required' },
                { label: 'Dates', done: !!this.purchaseOrderForm?.get('orderDate')?.valid && !!this.purchaseOrderForm?.get('expectedDate')?.valid, hint: this.dateLabel(this.purchaseOrderForm?.get('expectedDate')?.value) },
                { label: 'Lines', done: this.getPurchaseOrderLineControls.length > 0 && this.getPurchaseOrderLineControls.valid, hint: `${this.getPurchaseOrderLineControls.length} line(s)` }
            ];
        }
        return [
            { label: 'Main Party', done: !!this.purchaseOrderForm?.get('mainParty')?.value, hint: this.purchaseOrderForm?.get('mainParty')?.value?.entityName || 'Required' },
            { label: 'Sub Party', done: !!this.purchaseOrderForm?.get('subParty')?.value || !this.isCompanyMainParty(), hint: this.purchaseOrderForm?.get('subParty')?.value?.entityName || 'Optional contact' },
            { label: 'Terms', done: !!this.purchaseOrderForm?.get('paymentTerm')?.value, hint: this.purchaseOrderForm?.get('paymentTerm')?.value?.name || 'Required' },
            { label: 'Dates', done: !!this.purchaseOrderForm?.get('orderDate')?.valid && !!this.purchaseOrderForm?.get('expectedDate')?.valid, hint: this.dateLabel(this.purchaseOrderForm?.get('expectedDate')?.value) },
            { label: 'Lines', done: this.getPurchaseOrderLineControls.length > 0 && this.getPurchaseOrderLineControls.valid, hint: `${this.getPurchaseOrderLineControls.length} line(s)` }
        ];
    }

    formatMoney(amount: number | undefined | null): string {
        return amount !== undefined && amount !== null ? `${this.currencyCode} ${Number(amount).toFixed(currencyFractionDigits(this.currencyCode))}` : '-';
    }

    private syncVariantControl(lineGroup: FormGroup): void {
        const variantControl = lineGroup.get('variant');
        if (!variantControl) {
            return;
        }
        variantControl.enable({ emitEvent: false });
        variantControl.setValidators([Validators.required]);
        variantControl.updateValueAndValidity({ emitEvent: false });
    }

    private resolvePurchaseUnitPrice(product: Products | null | undefined, variant?: ProductVariant | null): number {
        return this.resolveAmount(
            variant?.buyingRateAdjustment,
            variant?.rateAdjustment,
            product?.buyingRate,
            product?.standardRate,
            0
        );
    }

    private resolveAmount(...candidates: Array<number | null | undefined>): number {
        for (const candidate of candidates) {
            if (candidate !== null && candidate !== undefined && !Number.isNaN(Number(candidate))) {
                return Number(candidate);
            }
        }
        return 0;
    }

    private dateLabel(value: Date | string | null | undefined): string {
        return value ? new Date(value).toLocaleDateString() : '-';
    }

    private syncLineFlexFieldValues(): void {
        const lineControls = this.getPurchaseOrderLineControls;
        const synced: FlexFieldValue[][] = [];
        for (let i = 0; i < lineControls.length; i++) {
            synced[i] = this.lineFlexFieldValues[i] ?? [];
        }
        this.lineFlexFieldValues = synced;
    }

    private extractFlexFieldValues(array: FormArray | null): FlexFieldValue[] {
        if (!array) {
            return [];
        }
        return array.controls.map(control => control.value as FlexFieldValue);
    }

    addPurchaseOrderLine() {
        this.getPurchaseOrderLineControls.push(this.createPurchaseOrderLineFormGroup());
        this.lineFlexFieldValues.push([]);
        this.syncLineFlexFieldValues();
    }

    removePurchaseOrderLine(index: number) {
        this.getPurchaseOrderLineControls.removeAt(index);
        if (index >= 0 && index < this.lineFlexFieldValues.length) {
            this.lineFlexFieldValues.splice(index, 1);
        }
        this.syncLineFlexFieldValues();
    }

    onProductChange(event: SelectChangeEvent, index: number) {
        if (event.value) {
            const selectedProduct = event.value as Products;
            const lineGroup = this.getPurchaseOrderLineControls.at(index);
            if (selectedProduct.productVariants && selectedProduct.productVariants.length > 0) {
                this.syncVariantControl(lineGroup as FormGroup);
                this.loadProductVariants(selectedProduct).subscribe(
                    () => {
                        lineGroup.patchValue({ description: selectedProduct.description, variant: null, unitPrice: 0 });
                        this.applyInventoryProfile(lineGroup as FormGroup, selectedProduct, null, 'PURCHASE');
                        this.syncClientItemCode(lineGroup as FormGroup, selectedProduct);
                    }
                );
            } else {
                this.syncVariantControl(lineGroup as FormGroup);
                lineGroup.patchValue({
                    description: selectedProduct.description,
                    variant: null,
                    unitPrice: this.resolvePurchaseUnitPrice(selectedProduct)
                });
                this.applyInventoryProfile(lineGroup as FormGroup, selectedProduct, null, 'PURCHASE');
                this.syncClientItemCode(lineGroup as FormGroup, selectedProduct);
            }
        }
    }

    onAiOrderDocumentSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file || this.isAiParsing) {
            return;
        }
        if (file.size > 15 * 1024 * 1024) {
            this.messageService.add({ severity: 'warn', summary: 'File too large', detail: 'Upload a document smaller than 15 MB.' });
            input.value = '';
            return;
        }

        this.aiExtraction = null;
        this.aiExtractionUnmatchedProducts = 0;
        this.aiExtractionCounterpartyMatched = false;
        this.isAiParsing = true;
        this.orderDocumentAiService.extract(file, 'PURCHASE_ORDER').subscribe({
            next: result => {
                this.pendingAiSourceDocument = file;
                const unmatchedProducts = this.applyAiOrderDocument(result);
                this.aiExtraction = result;
                this.aiExtractionUnmatchedProducts = unmatchedProducts;
                const warnings = result.warnings ?? [];
                const detail = unmatchedProducts
                    ? `Order draft populated. ${unmatchedProducts} line item(s) need a product selection.`
                    : 'Order draft populated from the uploaded document. Review the values before saving.';
                this.messageService.add({ severity: warnings.length ? 'warn' : 'success', summary: 'AI document parsed', detail });
                if (warnings.length) {
                    this.messageService.add({ severity: 'warn', summary: 'AI review required', detail: warnings.slice(0, 2).join(' ') });
                }
            },
            error: error => this.messageService.add({
                severity: 'error',
                summary: 'AI parse failed',
                detail: error?.error?.message || 'The document could not be parsed. Try a clear image or text PDF.'
            }),
            complete: () => {
                this.isAiParsing = false;
                input.value = '';
            }
        });
    }

    private applyAiOrderDocument(result: AiParsedOrderDocument): number {
        const header: Record<string, any> = {};
        const orderDate = this.parseAiDate(result.orderDate);
        const expectedDate = this.parseAiDate(result.expectedDate);
        const paymentTerm = this.findPaymentTerm(result.paymentTerms);

        if (orderDate) header['orderDate'] = orderDate;
        if (expectedDate) header['expectedDate'] = expectedDate;
        if (result.currencyCode) header['currencyCode'] = result.currencyCode.trim().toUpperCase();
        if (paymentTerm) header['paymentTerm'] = paymentTerm;
        if (result.documentNumber) header['supplierReference'] = result.documentNumber;
        if (result.notes) header['notes'] = result.notes;
        this.purchaseOrderForm.patchValue(header);

        this.aiExtractionCounterpartyMatched = !!(result.supplierName || result.counterpartyName) && this.applyAiVendor(result);
        if (!this.aiExtractionCounterpartyMatched) {
            this.setPartySourceMode('MANUAL');
        }
        const unmatchedProducts = this.replaceAiPurchaseOrderLines(result.lines ?? []);
        this.calculateTotals();
        return unmatchedProducts;
    }

    private applyAiVendor(result: AiParsedOrderDocument): boolean {
        const counterpartyName = result.supplierName || result.counterpartyName || '';
        if (this.crmActive()) {
            const client = this.crmClientValues.find(value => this.matchesAiText(
                counterpartyName,
                value.companyName,
                value.notifyingPersonName,
                value.clientNumber));
            if (client) {
                this.pendingAiContact = {
                    name: result.contactName,
                    email: result.contactEmail,
                    phone: result.contactPhone
                };
                this.onCrmClientPicked(client);
                return true;
            } else {
                this.messageService.add({ severity: 'warn', summary: 'CRM vendor not matched', detail: `Select the CRM vendor for "${counterpartyName}" manually.` });
            }
            return false;
        }

        const party = this.mainParties.map(option => option.value)
            .find(value => this.matchesAiText(counterpartyName, value?.entityName));
        if (party) {
            this.purchaseOrderForm.patchValue({ mainParty: party });
            this.onMainPartyChange({ value: party } as SelectChangeEvent);
            return true;
        } else {
            this.messageService.add({ severity: 'warn', summary: 'Party not matched', detail: `Select the party for "${counterpartyName}" manually.` });
            return false;
        }
    }

    private replaceAiPurchaseOrderLines(lines: AiParsedOrderLine[]): number {
        if (!lines.length) {
            return 0;
        }
        const lineGroups = lines.map(line => this.createAiPurchaseOrderLine(line));
                this.purchaseOrderForm.setControl('purchaseOrderLines', this.fb.array(lineGroups));
                this.refreshClientItemCodes();
        this.getPurchaseOrderLineControls.valueChanges.subscribe(() => this.calculateTotals());
        this.lineFlexFieldValues = lineGroups.map(() => []);
        this.syncLineFlexFieldValues();
        return lineGroups.filter(group => !group.get('product')?.value).length;
    }

    private createAiPurchaseOrderLine(line: AiParsedOrderLine): FormGroup {
        const product = this.findAiProduct(line);
        const group = this.createPurchaseOrderLineFormGroup();
        const quantity = this.aiNumber(line.quantity, 1);
        const unitPrice = this.aiNumber(line.unitPrice,
            this.aiDerivedUnitPrice(line, quantity, product ? this.resolvePurchaseUnitPrice(product) : 0));
        group.patchValue({
            product,
            description: line.description || product?.description || '',
            quantity,
            unitPrice
        }, { emitEvent: false });
        if (product) {
            this.syncVariantControl(group);
            this.applyInventoryProfile(group, product, null, 'PURCHASE');
        }
        return group;
    }

    private findAiProduct(line: AiParsedOrderLine): Products | null {
        const crmClientId = Number((this.purchaseOrderForm.get('crmClient')?.value as CrmClient | null)?.id || 0);
        return this.products.map(option => option.value).find(product =>
            product?.crmClientItemCodes?.some(mapping => mapping.active !== false
                && Number(mapping.crmClientId) === crmClientId
                && this.matchesAiText(line.itemCode || line.productCode, mapping.clientItemCode))
            || this.matchesAiText(line.itemCode || line.productCode, product?.productCode)
            || this.matchesAiText(line.description, product?.name)) ?? null;
    }

    quickMapClientItemCode(index: number): void {
        const lineGroup = this.getPurchaseOrderLineControls.at(index) as FormGroup;
        const product = lineGroup.get('product')?.value as Products | null;
        const crmClientId = this.selectedCrmClientId();
        const code = String(lineGroup.get('crmClientItemCode')?.value || '').trim();
        if (!product?.productID || !crmClientId || !code) {
            this.messageService.add({ severity: 'warn', summary: 'Client item code', detail: 'Select a CRM client and product, then enter the client item number.' });
            return;
        }
        this.quickMappingLine = index;
        const mappingId = Number(lineGroup.get('crmClientItemMappingId')?.value || 0);
        const userId = Number(this.sessionStorageService.getCustomer()?.id || this.sessionStorageService.getUser()?.id || 0);
        this.productsService.getProductById(product.productID).pipe(
            switchMap(fullProduct => {
                const mappings = [...(fullProduct.crmClientItemCodes || [])];
                const existingIndex = mappings.findIndex(mapping => Number(mapping.id) === mappingId
                    || (!mappingId && Number(mapping.crmClientId) === crmClientId));
                const mapping = { id: mappingId || undefined, crmClientId, clientItemCode: code, active: true };
                if (existingIndex >= 0) mappings[existingIndex] = { ...mappings[existingIndex], ...mapping };
                else mappings.push(mapping);
                fullProduct.crmClientItemCodes = mappings;
                fullProduct.updatedAt = new Date();
                fullProduct.updatedBy = userId || fullProduct.updatedBy;
                return this.productsService.updateProduct(product.productID, fullProduct);
            })
        ).subscribe({
            next: savedProduct => {
                Object.assign(product, savedProduct);
                lineGroup.patchValue({ product });
                this.syncClientItemCode(lineGroup, product);
                this.messageService.add({ severity: 'success', summary: 'Client item code', detail: 'Product mapping saved for the selected CRM client.' });
                this.quickMappingLine = null;
            },
            error: error => {
                this.quickMappingLine = null;
                this.messageService.add({ severity: 'error', summary: 'Client item code', detail: this.extractErrorDetail(error, 'Unable to save the product mapping.') });
            }
        });
    }

    private refreshClientItemCodes(): void {
        if (!this.purchaseOrderForm) return;
        this.getPurchaseOrderLineControls.controls.forEach(control => this.syncClientItemCode(control as FormGroup));
    }

    private syncClientItemCode(lineGroup: FormGroup, selectedProduct?: Products | null): void {
        const product = selectedProduct || lineGroup.get('product')?.value as Products | null;
        const crmClientId = this.selectedCrmClientId();
        const mapping = product?.crmClientItemCodes?.find(item => item.active !== false && Number(item.crmClientId) === crmClientId);
        lineGroup.patchValue({ crmClientItemCode: mapping?.clientItemCode || '', crmClientItemMappingId: mapping?.id || null }, { emitEvent: false });
    }

    private selectedCrmClientId(): number {
        return Number((this.purchaseOrderForm?.get('crmClient')?.value as CrmClient | null)?.id || 0);
    }

    private findPaymentTerm(value?: string | null): PaymentTerm | null {
        return this.paymentTerms.map(option => option.value)
            .find(term => this.matchesAiText(value, term?.name)) ?? null;
    }

    private parseAiDate(value?: string | null): Date | null {
        if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
            return null;
        }
        const date = new Date(`${value.trim()}T00:00:00`);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    private aiNumber(value: number | null | undefined, fallback: number): number {
        return value !== null && value !== undefined && Number.isFinite(Number(value)) ? Number(value) : fallback;
    }

    private aiDerivedUnitPrice(line: AiParsedOrderLine, quantity: number, fallback: number): number {
        const netAmount = this.aiNumber(line.netAmount, this.aiNumber(line.lineTotal, 0));
        return quantity > 0 && netAmount > 0 ? netAmount / quantity : fallback;
    }

    private matchesAiText(value: string | null | undefined, ...candidates: Array<string | null | undefined>): boolean {
        const normalizedValue = this.normalizeAiText(value);
        return !!normalizedValue && candidates.some(candidate => normalizedValue === this.normalizeAiText(candidate));
    }

    private normalizeAiText(value: string | null | undefined): string {
        return (value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    }

    loadProductVariants(product: Products) {
        return of(product.productVariants).pipe(
            tap(productVariants => {
                this.productVariants = productVariants.map(variant => ({
                    value: variant,
                    label: variant.name
                }));
            })
        )
    }

    onProductVariantChange(event: SelectChangeEvent, index: number) {
        if (event.value) {
            const selectedProductVariant = event.value as ProductVariant;
            const lineGroup = this.getPurchaseOrderLineControls.at(index);
            const selectedProduct = lineGroup.get('product')?.value as Products | null;
            lineGroup.patchValue({ unitPrice: this.resolvePurchaseUnitPrice(selectedProduct, selectedProductVariant) });
            if (selectedProduct) this.applyInventoryProfile(lineGroup as FormGroup, selectedProduct, selectedProductVariant, 'PURCHASE');
        }
    }

    private applyInventoryProfile(lineGroup: FormGroup, product: Products, variant: ProductVariant | null, usage: 'PURCHASE'): void {
        const profile = product.inventoryProfiles?.find(row => row.variantId === variant?.variantID)
            || (!variant ? product.inventoryProfiles?.find(row => row.variantId == null) : undefined);
        if (!product.inventoryManaged || !profile) {
            lineGroup.patchValue({ inventoryItemId: null, orderUomId: null, baseUomId: null,
                conversionFactorToBase: 1, orderedBaseQuantity: null }, { emitEvent: false });
            return;
        }
        const orderUomId = profile.defaultPurchaseUomId || profile.baseUomId;
        const conversion = profile.uomConversions?.find(row => row.uomId === orderUomId
            && (row.usageType === usage || row.usageType === 'BOTH'));
        const factor = orderUomId === profile.baseUomId ? 1 : Number(conversion?.factorToBase || 1);
        const quantity = Number(lineGroup.get('quantity')?.value || 0);
        lineGroup.patchValue({ inventoryItemId: profile.warehouseItemId, orderUomId, baseUomId: profile.baseUomId,
            conversionFactorToBase: factor, orderedBaseQuantity: quantity * factor }, { emitEvent: false });
    }

    savePurchaseOrder() {
        if (this.isLocked) {
            this.messageService.add({
                severity: 'error',
                summary: 'Order Locked',
                detail: this.lockReason || 'This purchase order is locked from editing because a vendor bill has been generated.'
            });
            return;
        }
        if (this.purchaseOrderForm.invalid) {
            this.purchaseOrderForm.markAllAsTouched();
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Please correct required header fields and line errors before saving.'
            });
            return;
        }
        const selectedMainParty = this.purchaseOrderForm.get('mainParty')?.value as Party | null;
        const selectedCrmClient = this.purchaseOrderForm.get('crmClient')?.value as CrmClient | null;
        if (this.isCrmPartySource() && !selectedCrmClient?.id) {
            this.messageService.add({ severity: 'error', summary: 'CRM vendor required', detail: 'Select a CRM vendor for this purchase order.' });
            return;
        }
        if (this.isManualPartySource() && !selectedMainParty?.id?.entityId) {
            this.messageService.add({
                severity: 'error',
                summary: 'Main party required',
                detail: 'Select the party for this purchase order.'
            });
            return;
        }
        if (this.getPurchaseOrderLineControls.length === 0) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Please add at least one purchase order line.'
            });
            return;
        }
        this.isLoading = true;
        this.calculateTotals();
        const formValue = this.purchaseOrderForm.getRawValue();
        const purchaseOrder = { ...formValue } as PurchaseOrder;
        purchaseOrder.supplier = null as any;
        purchaseOrder.purchaseOrderNumber = formValue.orderNumber;
        const crmClientPayload = formValue.crmClient as CrmClient | null;
        if (this.isCrmPartySource()) {
            purchaseOrder.crmClient = this.minimalCrmClientRef(crmClientPayload?.id);
            (purchaseOrder as any).crmClientId = crmClientPayload?.id ?? null;
            purchaseOrder.crmAccountId = formValue.crmAccountId ?? null;
            purchaseOrder.crmClientContactId = formValue.crmClientContactId ?? null;
            purchaseOrder.mainParty = undefined;
            purchaseOrder.subParty = undefined;
        } else {
            purchaseOrder.crmClient = null as any;
            purchaseOrder.crmClientId = null as any;
            purchaseOrder.crmAccountId = null as any;
            purchaseOrder.crmClientContactId = null as any;
            purchaseOrder.mainParty = this.minimalPartyRef(formValue.mainParty);
            purchaseOrder.subParty = this.minimalPartyRef(formValue.subParty);
        }
        purchaseOrder.paymentTerm = this.minimalPaymentTermRef(formValue.paymentTerm?.paymentTermID) as PaymentTerm;
        const extRef = (formValue.externalReferenceNumber || formValue.supplierReference || '').trim();
        purchaseOrder.externalReferenceNumber = extRef || undefined;
        purchaseOrder.supplierReference = extRef || undefined;
        purchaseOrder.company = null as any;
        delete (purchaseOrder as any).vendor;
        delete (purchaseOrder as any).orderNumber;
        purchaseOrder.owningCompanyId = this.sessionStorageService.getCompany()!.id;
        purchaseOrder.state = this.editMode ? (this.currentPurchaseOrder?.state ?? 'DRAFT') : 'DRAFT';
        purchaseOrder.approvalStatus = this.currentPurchaseOrder?.approvalStatus ?? 'NOT_REQUIRED';
        purchaseOrder.receivingStatus = this.currentPurchaseOrder?.receivingStatus ?? 'NOT_RECEIVED';
        purchaseOrder.billingStatus = this.currentPurchaseOrder?.billingStatus ?? 'NOT_BILLED';
        purchaseOrder.matchingStatus = this.currentPurchaseOrder?.matchingStatus ?? 'NOT_STARTED';
        purchaseOrder.holdStatus = this.currentPurchaseOrder?.holdStatus ?? 'CLEAR';
        purchaseOrder.versionNumber = this.currentPurchaseOrder?.versionNumber;
        purchaseOrder.logisticsProjectId = formValue.logisticsProjectId || undefined;
        purchaseOrder.currencyCode = formValue.currencyCode || this.currencyCode || 'SAR';
        purchaseOrder.exchangeRate = formValue.exchangeRate || 1;
        const userId = this.sessionStorageService.getUser()!.id;
        purchaseOrder.flexFieldValues = this.extractFlexFieldValues(this.purchaseOrderForm.get('flexFields') as FormArray);
        delete (purchaseOrder as any).flexFields;
        purchaseOrder.purchaseOrderLines = purchaseOrder.purchaseOrderLines ?? [];
        purchaseOrder.purchaseOrderLines.forEach((line, index) => {
            const lineControl = this.getPurchaseOrderLineControls.at(index) as FormGroup;
            delete (line as any).crmClientItemCode;
            delete (line as any).crmClientItemMappingId;
            delete (line as any).displayUnitPrice;
            line.flexFieldValues = this.extractFlexFieldValues(lineControl.get('flexFields') as FormArray);
            delete (line as any).flexFields;
            const quantity = Number(line.quantity || 0);
            const unitPrice = Number(line.unitPrice || 0);
            const taxRate = Number(line.product?.taxRate || 0);
            const amounts = orderLineAmounts(quantity, unitPrice, taxRate, this.currencyCode, Number(line.appliedLineDiscount || 0));
            line.unitTaxAmount = amounts.tax / (quantity || 1);
            line.unitDiscount = amounts.discount / (quantity || 1);
            line.unitLineAmount = unitPrice;
            line.appliedLineDiscount = amounts.discount;
            line.totalTax = amounts.tax;
            line.totalLineAmount = amounts.total;
            line.taxRate = taxRate;
            line.taxCode = taxRate > 0 ? 'VAT' : 'NO_TAX';
            line.taxInclusive = false;
            line.taxJurisdiction = 'SA';
            line.priceSource = line.priceSource || 'MANUAL';
            line.manualPriceOverride = false;
            line.logisticsProjectId = line.logisticsProjectId || purchaseOrder.logisticsProjectId;
            line.product = this.minimalProductRef(line.product?.productID) as Products;
            line.variant = this.minimalVariantRef((line as any).variant?.variantID) as any;
        });
        purchaseOrder.subtotalAmount = this.subtotal;
        purchaseOrder.discountAmount = this.getPurchaseOrderLineControls.controls.reduce((sum, line) => sum + this.lineAmounts(line).discount, 0);
        purchaseOrder.taxAmount = this.tax;
        purchaseOrder.totalAmount = this.total;
        purchaseOrder.baseCurrencyAmount = this.total * (purchaseOrder.exchangeRate || 1);

        if (this.editMode) {
            purchaseOrder.purchaseOrderID = this.purchaseOrderId;
            purchaseOrder.updatedAt = new Date();
            purchaseOrder.updatedBy = userId;
            purchaseOrder.purchaseOrderLines.forEach(line => {
                line.orderID = this.purchaseOrderId;
                line.updatedAt = new Date();
                line.updatedBy = userId;
                if (!line.lineID) {
                    line.lineNumber = (purchaseOrder.purchaseOrderLines.indexOf(line) + 1).toString();
                    line.createdAt = new Date();
                    line.createdBy = userId;
                }
            });
        } else {
            purchaseOrder.createdAt = new Date();
            purchaseOrder.createdBy = userId;
            purchaseOrder.updatedAt = new Date();
            purchaseOrder.updatedBy = userId;
            purchaseOrder.purchaseOrderLines.forEach(line => {
                line.lineNumber = (purchaseOrder.purchaseOrderLines.indexOf(line) + 1).toString();
                line.createdAt = new Date();
                line.createdBy = userId;
            });
        }

        const operation = this.editMode
            ? this.purchaseOrderService.updatePurchaseOrder(this.purchaseOrderId, purchaseOrder)
            : this.purchaseOrderService.createPurchaseOrder(purchaseOrder);

        operation.subscribe({
            next: (newPurchaseOrder) => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Purchase order saved successfully.'
                });
                const savedOrderId = Number(newPurchaseOrder?.purchaseOrderID || this.purchaseOrderId || 0);
                const complete = () => {
                    this.isLoading = false;
                    if (!this.editMode && savedOrderId) {
                        this.router.navigate(['/order/purchase-order/view', savedOrderId]);
                        return;
                    }
                    this.router.navigate(['/order/purchase-order']);
                };
                this.persistAiSourceDocument(savedOrderId).subscribe({
                    next: () => this.linkProjectRequirementFulfillment(newPurchaseOrder).add(complete),
                    error: error => {
                        this.messageService.add({ severity: 'warn', summary: 'Source document', detail: error?.error?.message || 'Purchase order was saved, but its AI source document could not be attached.' });
                        this.linkProjectRequirementFulfillment(newPurchaseOrder).add(complete);
                    }
                });
            },
            error: (error) => {
                this.isLoading = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Save failed',
                    detail: this.extractErrorDetail(error, 'Unable to save the purchase order.')
                });
            }
        })
    }

    private linkProjectRequirementFulfillment(savedOrder?: PurchaseOrder) {
        const projectId = Number(this.route.snapshot.queryParamMap.get('projectId') || this.purchaseOrderForm.get('logisticsProjectId')?.value || 0);
        const requirementId = Number(this.route.snapshot.queryParamMap.get('requirementId') || 0);
        const owningCompanyId = Number(this.sessionStorageService.getCompany()?.id || 0);
        const targetResourceId = Number(savedOrder?.purchaseOrderID || this.purchaseOrderId || 0);
        if (!projectId || !requirementId || !owningCompanyId || !targetResourceId) {
            return { add: (callback: () => void) => callback() };
        }
        return this.logisticsProjectsService.linkRequirementFulfillment(projectId, owningCompanyId, requirementId, {
            targetResourceType: this.route.snapshot.queryParamMap.get('targetResourceType') || 'PURCHASE_ORDER',
            targetResourceId,
            sourceModule: 'ORDER',
            sourceReference: savedOrder?.purchaseOrderNumber || `Purchase order #${targetResourceId}`,
            fulfilledQuantity: Number(this.route.snapshot.queryParamMap.get('quantity') || 1),
            fulfillmentStatus: 'LINKED',
            status: 'ACTIVE'
        }).subscribe({
            error: error => this.messageService.add({
                severity: 'warn',
                summary: 'Requirement link',
                detail: error?.error?.message || 'Purchase order was saved, but requirement fulfillment was not linked.'
            })
        });
    }

    private persistAiSourceDocument(purchaseOrderId: number) {
        const file = this.pendingAiSourceDocument;
        const owningCompanyId = Number(this.sessionStorageService.getCompany()?.id || 0);
        const uploadedBy = Number(this.sessionStorageService.getUser()?.id || 0);
        if (!file || !purchaseOrderId || !owningCompanyId || !uploadedBy) return of(void 0);
        return this.firebaseStorageService.uploadFileAndGetPublicUrl(file, `orders/purchase-orders/${purchaseOrderId}/attachments`).pipe(
            switchMap(filePath => this.purchaseOrderService.createPurchaseOrderAttachment(purchaseOrderId, owningCompanyId, {
                attachmentId: 0, purchaseOrderId, owningCompanyId, fileName: file.name, filePath,
                fileType: file.type || 'application/pdf', fileSize: file.size, uploadedAt: new Date(), uploadedBy,
                fileContent: 'Source document uploaded for AI extraction'
            } as PurchaseOrderAttachment)),
            tap(() => this.pendingAiSourceDocument = undefined),
            map(() => void 0)
        );
    }

    private extractErrorDetail(error: any, fallback: string): string {
        return error?.error?.message || error?.error?.error || error?.message || fallback;
    }

    cancel() {
        this.location.back();
    }

    lineAmounts(line: AbstractControl) {
        const quantity = Number(line.get('quantity')?.value || 0);
        const unitPrice = Number(line.get('unitPrice')?.value || 0);
        const product = line.get('product')?.value as Products | null;
        return orderLineAmounts(quantity, unitPrice, Number(product?.taxRate || 0), this.currencyCode,
            Number(line.get('appliedLineDiscount')?.value || 0));
    }

    openPriceCalculator(line: AbstractControl): void {
        this.priceTargetLine = line;
        const amounts = this.lineAmounts(line);
        this.targetNetAmount = roundCurrency(amounts.subtotal - amounts.discount, this.currencyCode);
    }

    get currencyDigits(): number { return currencyFractionDigits(this.currencyCode); }

    priceTargetPreview(): { price: number; net: number; tax: number; total: number } | null {
        const line = this.priceTargetLine;
        const target = this.targetNetAmount;
        if (!line || target === null || target <= 0) return null;
        const quantity = Number(line.get('quantity')?.value);
        const discount = Number(line.get('appliedLineDiscount')?.value || 0);
        const price = unitPriceForTargetNet(quantity, target, discount, this.currencyCode);
        if (price === null) return null;
        const product = line.get('product')?.value as Products | null;
        const amounts = orderLineAmounts(quantity, price, Number(product?.taxRate || 0), this.currencyCode, discount);
        return { price, net: roundCurrency(amounts.subtotal - amounts.discount, this.currencyCode), tax: amounts.tax, total: amounts.total };
    }

    applyTargetPrice(): void {
        const preview = this.priceTargetPreview();
        if (!preview || !this.priceTargetLine) return;
        this.priceTargetLine.get('unitPrice')?.setValue(preview.price);
        this.priceTargetLine.markAsDirty();
        this.priceTargetLine = null;
    }

    calculateTotals(): void {
        let subtotal = 0;
        let discount = 0;
        let totalTax = 0;
        this.getPurchaseOrderLineControls.controls.forEach(line => {
            const amounts = this.lineAmounts(line);
            subtotal += amounts.subtotal;
            discount += amounts.discount;
            totalTax += amounts.tax;
        });
        this.subtotal = roundCurrency(subtotal, this.currencyCode);
        this.tax = roundCurrency(totalTax, this.currencyCode);
        this.total = roundCurrency(this.subtotal - discount + this.tax, this.currencyCode);
    }

    private minimalCrmClientRef(id?: number | null): CrmClient | undefined {
        return id ? ({ id } as CrmClient) : undefined;
    }

    private minimalCustomerRef(id?: number | null): Customer | undefined {
        return id ? ({ id } as Customer) : undefined;
    }

    private minimalCompanyRef(id?: number | null): Company | undefined {
        return id ? ({ id } as Company) : undefined;
    }

    private minimalPaymentTermRef(paymentTermID?: number | null): PaymentTerm | undefined {
        return paymentTermID ? ({ paymentTermID } as PaymentTerm) : undefined;
    }

    private minimalProductRef(productID?: number | null): Products | undefined {
        return productID ? ({ productID } as Products) : undefined;
    }

    private minimalVariantRef(variantID?: number | null): ProductVariant | undefined {
        return variantID ? ({ variantID } as ProductVariant) : undefined;
    }

    openCompanyCreation(): void {
        this.selectedCompany = new Company();
        this.companyFormHeader = 'Create Company';
        this.companyFormSubmitLabel = 'Create';
        this.showCompanyForm = true;
    }

    onCompanyFormClosed(result?: Company | boolean): void {
        this.showCompanyForm = false;
        const savedCompany = result instanceof Company ? result : undefined;
        if (savedCompany) {
            this.loadCompanies().subscribe(() => {
                const currentCompany = this.purchaseOrderForm.get('company')?.value as Company | null;
                if (currentCompany) {
                    const matching = this.companies.find(item => item.value?.id === currentCompany.id);
                    if (!matching) {
                        this.purchaseOrderForm.patchValue({ company: currentCompany });
                    }
                    this.onCompanyChange({ value: currentCompany } as SelectChangeEvent);
                }
            });
        }
    }

    openVendorCreation(): void {
        const company = this.purchaseOrderForm.get('company')?.value as Company | null;
        if (!company) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Select Company',
                detail: 'Choose a company before creating a vendor.'
            });
            return;
        }
        this.selectedVendor = new Customer();
        this.selectedCompany = company;
        this.vendorFormHeader = 'Create Vendor';
        this.vendorFormSubmitLabel = 'Create';
        this.showVendorForm = true;
    }

    onVendorFormClosed(result?: Customer | boolean): void {
        this.showVendorForm = false;
        const savedVendor = result instanceof Customer ? result : undefined;
        if (savedVendor) {
            this.selectedVendor = savedVendor;
            const company = this.purchaseOrderForm.get('company')?.value as Company | null;
            if (company) {
                this.loadVendorsByCompany(company).subscribe(() => {
                    if (this.selectedVendor?.id) {
                        this.purchaseOrderForm.patchValue({ vendor: this.selectedVendor });
                    }
                });
            }
        }
    }

    openCreateProductDialog(lineIndex: number): void {
        this.productDialogRef?.close();
        this.productDialogRef = this.dialogService.open(AddEditProductComponent, {
            header: 'Add Product',
            modal: true,
            width: '96vw',
            breakpoints: { '1400px': '96vw', '960px': '98vw', '640px': '100vw' },
            contentStyle: { 'max-height': 'calc(100vh - 7rem)', overflow: 'auto' },
            styleClass: 'order-product-dialog'
        });
        this.productDialogRef.onClose.subscribe((createdProduct: Products | undefined) => {
            this.productDialogRef = undefined;
            if (!createdProduct?.productID) {
                return;
            }
            this.selectCreatedProduct(createdProduct, lineIndex);
        });
    }

    private selectCreatedProduct(product: Products, lineIndex: number): void {
        const existingIndex = this.products.findIndex(option => Number(option.value?.productID) === Number(product.productID));
        const option = { label: product.name, value: product };
        this.products = existingIndex >= 0
            ? this.products.map((item, index) => index === existingIndex ? option : item)
            : [...this.products, option];

        const lineGroup = this.getPurchaseOrderLineControls.at(lineIndex) as FormGroup | undefined;
        if (!lineGroup) {
            return;
        }
        lineGroup.patchValue({ product }, { emitEvent: false });
        lineGroup.get('product')?.markAsDirty();
        lineGroup.get('product')?.markAsTouched();
        this.onProductChange({ value: product } as SelectChangeEvent, lineIndex);
        this.messageService.add({ severity: 'success', summary: 'Product added', detail: `${product.name} was selected for line ${lineIndex + 1}.` });
    }

    navigateToCreateProductVariant(product?: Products | null): void {
        if (product?.productID) {
            const url = this.router.serializeUrl(this.router.createUrlTree(['/order/products/edit', product.productID]));
            window.open(url, '_blank');
            return;
        }
        this.messageService.add({
            severity: 'warn',
            summary: 'Select Product',
            detail: 'Choose a product before creating variants.'
        });
    }

    private crmClientToSupplierOption(client: CrmClient): SelectItem<Customer> {
        const supplier = this.crmClientToLinkedCustomer(client);
        const roles = (client.roles || []).join(', ');
        const missingLink = !supplier.id ? ' - sync contact required' : '';
        return {
            value: supplier,
            label: `${client.companyName || client.notifyingPersonName || 'CRM Client'}${client.clientNumber ? ` (${client.clientNumber})` : ''}${roles ? ` - ${roles}` : ' - Supplier'}${missingLink}`,
            disabled: !supplier.id
        };
    }

    private crmClientToLinkedCustomer(client: CrmClient): Customer {
        const customer = new Customer();
        customer.id = client.linkedCustomerId || 0;
        customer.name = client.companyName || client.notifyingPersonName || `CRM Client ${client.id || ''}`.trim();
        customer.emailAddress = client.primaryEmail || '';
        customer.phoneNumber = client.phoneNumber || client.mobileNumber || '';
        customer.countryId = client.countryId || 0;
        const company = new Company();
        company.id = client.linkedCompanyId || 0;
        company.name = client.companyName || customer.name;
        company.countryId = client.countryId || 0;
        company.emailAddress = client.primaryEmail || null;
        company.phoneNumber = client.phoneNumber || client.mobileNumber || null;
        company.websiteURL = client.website || null;
        company.taxRegNumber = client.taxRegNumber || null;
        company.crNumber = Number(client.crNumber || 0);
        customer.company = company;
        return customer;
    }

    crmActive(): boolean {
        return this.enterpriseFeatureService.getIsCrmSubscribed();
    }

    crmContactLabel(contact: CrmClientCommunication): string {
        return [
            contact.name,
            contact.communicationType,
            contact.email,
            contact.phone || contact.mobile
        ].filter(Boolean).join(' | ') || 'CRM contact';
    }

    crmContactOptions(): SelectItem<number>[] {
        return this.crmClientContacts
            .map(contact => ({
                label: this.crmContactLabel(contact),
                value: contact.id || 0
            }))
            .filter(option => !!option.value);
    }

    selectedCrmContactLabel(): string {
        const selectedId = Number(this.purchaseOrderForm?.get('crmClientContactId')?.value || 0);
        const selected = this.crmClientContacts.find(contact => contact.id === selectedId);
        if (selected) {
            return this.crmContactLabel(selected);
        }
        return this.crmClientContacts.length ? 'Select a CRM contact' : 'No contacts linked';
    }

    private resolvePreferredCrmContactId(selectedId?: number | null): number | null {
        const normalizedId = Number(selectedId || 0);
        if (normalizedId && this.crmClientContacts.some(contact => contact.id === normalizedId)) {
            return normalizedId;
        }
        return this.crmClientContacts.find(contact => contact.primaryRecord)?.id
            || this.crmClientContacts[0]?.id
            || null;
    }

    private applyPartyValidators(): void {
        const mainPartyControl = this.purchaseOrderForm?.get('mainParty');
        const companyControl = this.purchaseOrderForm?.get('company');
        const vendorControl = this.purchaseOrderForm?.get('vendor');
        const crmClientControl = this.purchaseOrderForm?.get('crmClient');
        if (!mainPartyControl || !companyControl || !vendorControl || !crmClientControl) {
            return;
        }
        if (this.isCrmPartySource()) {
            mainPartyControl.clearValidators();
            crmClientControl.setValidators([Validators.required]);
        } else {
            mainPartyControl.setValidators([Validators.required]);
            crmClientControl.clearValidators();
        }
        companyControl.clearValidators();
        vendorControl.clearValidators();
        mainPartyControl.updateValueAndValidity({ emitEvent: false });
        companyControl.updateValueAndValidity({ emitEvent: false });
        vendorControl.updateValueAndValidity({ emitEvent: false });
        crmClientControl.updateValueAndValidity({ emitEvent: false });
    }

    private loadPartyOptionsForMode(): void {
        this.loadMainParties();
        if (this.crmActive()) {
            if (!this.editMode) {
                this.partySourceMode = 'CRM';
                this.applyPartyValidators();
            }
            this.loadCrmClients().subscribe();
        }
    }

    isCrmPartySource(): boolean {
        return this.partySourceMode === 'CRM';
    }

    isManualPartySource(): boolean {
        return this.partySourceMode === 'MANUAL';
    }

    setPartySourceMode(mode: PartySourceMode): void {
        if (mode === 'CRM' && !this.crmActive()) {
            return;
        }
        this.partySourceMode = mode;
        if (mode === 'CRM') {
            this.purchaseOrderForm.patchValue({ mainParty: null, subParty: null, company: null, vendor: null }, { emitEvent: false });
            this.loadCrmClients().subscribe();
        } else {
            this.crmClientContacts = [];
            this.purchaseOrderForm.patchValue({
                crmClient: null,
                crmClientContactId: null,
                mainParty: null,
                subParty: null,
                company: null,
                vendor: null
            }, { emitEvent: false });
            this.loadMainParties();
        }
        this.applyPartyValidators();
        this.refreshClientItemCodes();
    }

    private minimalPartyRef(party?: Party | null): Party | undefined {
        if (!party?.id?.entityId || !party.id.entityType) {
            return undefined;
        }
        return {
            id: { entityId: party.id.entityId, entityType: party.id.entityType },
            owningCompanyId: this.sessionStorageService.getCompany()?.id ?? 0,
            entityName: party.entityName,
            isCompany: party.isCompany
        };
    }
}
